import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Users,
    UserPlus,
    Mail,
    Shield,
    Trash2,
    Loader2,
    Eye,
    EyeOff,
    CheckCircle,
    AlertCircle,
    Key
} from 'lucide-react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../components/ui/toast';

const ADMINS_KEY = 'siteAdmins';

const UsersPage = () => {
    const [admins, setAdmins] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        role: 'admin'
    });
    const [error, setError] = useState('');
    const toast = useToast();

    useEffect(() => {
        loadAdmins();
    }, []);

    const loadAdmins = () => {
        try {
            const stored = localStorage.getItem(ADMINS_KEY);
            if (stored) {
                setAdmins(JSON.parse(stored));
            }
        } catch (e) {
            console.error('Error loading admins:', e);
        }
    };

    const saveAdmins = (adminList) => {
        localStorage.setItem(ADMINS_KEY, JSON.stringify(adminList));
        setAdmins(adminList);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!formData.email || !formData.password || !formData.name) {
            setError('Please fill in all required fields');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(
                auth,
                formData.email,
                formData.password
            );

            // Save admin info locally
            const newAdmin = {
                id: userCredential.user.uid,
                email: formData.email,
                name: formData.name,
                role: formData.role,
                created_at: new Date().toISOString(),
                active: true
            };

            const updatedAdmins = [...admins, newAdmin];
            saveAdmins(updatedAdmins);

            toast.success(`Admin user "${formData.name}" created successfully!`);
            setShowAddForm(false);
            setFormData({ email: '', password: '', confirmPassword: '', name: '', role: 'admin' });
        } catch (err) {
            console.error('Error creating user:', err);
            let errorMessage = 'Failed to create user';
            if (err.code === 'auth/email-already-in-use') {
                errorMessage = 'This email is already registered';
            } else if (err.code === 'auth/invalid-email') {
                errorMessage = 'Invalid email address';
            } else if (err.code === 'auth/weak-password') {
                errorMessage = 'Password is too weak';
            }
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const toggleUserStatus = (id) => {
        const updatedAdmins = admins.map(admin =>
            admin.id === id ? { ...admin, active: !admin.active } : admin
        );
        saveAdmins(updatedAdmins);
        toast.success('User status updated');
    };

    const deleteUser = (id) => {
        if (window.confirm('Are you sure you want to remove this admin? They will still be able to login via Firebase unless deleted from Firebase Console.')) {
            const updatedAdmins = admins.filter(admin => admin.id !== id);
            saveAdmins(updatedAdmins);
            toast.success('Admin removed from list');
        }
    };

    return (
        <AdminLayout title="User Management">
            <Helmet>
                <title>Users - Admin Dashboard</title>
            </Helmet>

            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                    <p className="text-gray-400">Manage admin users and access permissions</p>
                </div>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-gradient-to-r from-teal-500 to-blue-500 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-teal-400 hover:to-blue-400 transition-all shadow-lg shadow-teal-500/25 flex items-center gap-2"
                >
                    <UserPlus className="w-5 h-5" />
                    Add Admin User
                </button>
            </div>

            {/* Add User Form */}
            {showAddForm && (
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 mb-6">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-teal-400" />
                        Create New Admin User
                    </h3>

                    {error && (
                        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Full Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                placeholder="John Doe"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                placeholder="admin@example.com"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Password *</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                    placeholder="••••••••"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Confirm Password *</label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                placeholder="••••••••"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Role</label>
                            <select
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                            >
                                <option value="admin" className="bg-slate-800">Admin</option>
                                <option value="editor" className="bg-slate-800">Editor</option>
                                <option value="viewer" className="bg-slate-800">Viewer</option>
                            </select>
                        </div>

                        <div className="flex items-end gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-gradient-to-r from-teal-500 to-blue-500 text-white px-5 py-3 rounded-xl font-semibold hover:from-teal-400 hover:to-blue-400 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-5 h-5" />
                                        Create User
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowAddForm(false)}
                                className="px-5 py-3 bg-white/5 text-gray-400 rounded-xl hover:bg-white/10 transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Users List */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                <div className="p-4 border-b border-white/10">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-teal-400" />
                        Admin Users ({admins.length})
                    </h3>
                </div>

                <div className="divide-y divide-white/5">
                    {admins.length > 0 ? (
                        admins.map(admin => (
                            <div key={admin.id} className="p-4 flex items-center justify-between hover:bg-white/5 transition-all">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                                        {admin.name?.[0]?.toUpperCase() || 'A'}
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">{admin.name}</p>
                                        <p className="text-sm text-gray-400 flex items-center gap-1">
                                            <Mail className="w-3 h-3" />
                                            {admin.email}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${admin.role === 'admin'
                                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                        : admin.role === 'editor'
                                            ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                                            : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                                        }`}>
                                        <Shield className="w-3 h-3 inline mr-1" />
                                        {admin.role}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${admin.active
                                        ? 'bg-emerald-500/20 text-emerald-300'
                                        : 'bg-red-500/20 text-red-300'
                                        }`}>
                                        {admin.active ? 'Active' : 'Inactive'}
                                    </span>
                                    <button
                                        onClick={() => toggleUserStatus(admin.id)}
                                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                                        title={admin.active ? 'Deactivate' : 'Activate'}
                                    >
                                        <Key className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => deleteUser(admin.id)}
                                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                        title="Remove"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="p-8 text-center">
                            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">No admin users added yet</p>
                            <p className="text-sm text-gray-500 mt-1">Click "Add Admin User" to create new admins</p>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};

export default UsersPage;
